import React, { useEffect, useRef, useState } from 'react'
import * as echarts from 'echarts'
import { Card, CardContent, Button } from '@duck/ui'
import { useTar } from '@/modules'
import { apis } from '@/modules/apiClients'
import { StandardLoader } from '../StandardLoader'
import { NoDataEmptyState } from '../EmptyStates'
import { TaskDataInput } from '../DataInputs/TaskDataInput'

type ChallengesBucketMode = 'daily' | 'weekly'
type SignupsRange = 'last7' | 'all'
type ChallengesRange = 'last7' | 'all'
type TasksRange = 'top5' | 'all'

export const AdminStatistics: React.FC = () => {
  const { t } = useTar()

  const [data, setData] = useState<import('@duck/api-definitions').UserService.AdminStatisticsResponse | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [challengesMode, setChallengesMode] = useState<ChallengesBucketMode>('daily')
  const [signupsRange, setSignupsRange] = useState<SignupsRange>('last7')
  const [challengesRange, setChallengesRange] = useState<ChallengesRange>('last7')
  const [tasksRange, setTasksRange] = useState<TasksRange>('top5')
  // Empty = all tasks (default). singlePick means at most one id when narrowed.
  const [gradeDistributionTaskIds, setGradeDistributionTaskIds] = useState<string[]>([])

  const signupsRef = useRef<HTMLDivElement | null>(null)
  const challengesRef = useRef<HTMLDivElement | null>(null)
  const tasksCountRef = useRef<HTMLDivElement | null>(null)
  const tasksGradeRef = useRef<HTMLDivElement | null>(null)
  const gradeDistributionRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    let cancelled = false

    const fetchData = async () => {
      try {
        setIsLoading(true)
        setError(null)
        const response = await apis.userService.adminStatisticsGet()
        if (!cancelled) setData(response.data)
      } catch (err) {
        if (!cancelled) {
          console.error('Error fetching admin statistics:', err)
          setError(err instanceof Error ? err.message : 'Failed to fetch statistics')
        }
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }

    void fetchData()

    return () => { cancelled = true }
  }, [])

  const formatDateLabel = (isoString: string) => {
    const date = new Date(isoString)
    if (Number.isNaN(date.getTime())) {
      return isoString
    }
    return date.toLocaleDateString()
  }

  const formatWeekLabel = (isoString: string) => {
    const date = new Date(isoString)
    if (Number.isNaN(date.getTime())) {
      return isoString
    }
    return t('admin.statisticsWeekOfDATE', { date: date.toLocaleDateString() })
  }

  // User signups over time (single-series bar chart)
  useEffect(() => {
    if (!signupsRef.current || !data || data.userSignupsOverTime.length === 0) {
      return
    }

    let signups = data.userSignupsOverTime
    if (signupsRange === 'last7' && signups.length > 7) {
      signups = signups.slice(-7)
    }

    const instance = echarts.init(signupsRef.current)

    const labels = signups.map(point => formatDateLabel(point.date))
    const counts = signups.map(point => point.count ?? 0)

    const option: echarts.EChartsOption = {
      tooltip: {
        trigger: 'axis'
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: labels,
        axisLabel: {
          rotate: 45
        }
      },
      yAxis: {
        type: 'value'
      },
      series: [
        {
          name: t('admin.statisticsUserSignupsSeries'),
          type: 'bar',
          data: counts,
        }
      ]
    }

    instance.setOption(option)

    const handleResize = () => {
      instance.resize()
    }
    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
      instance.dispose()
    }
  }, [data, signupsRange, t])

  // Challenges over time (two-series bar chart, daily/weekly)
  useEffect(() => {
    if (!challengesRef.current || !data) {
      return
    }

    let buckets =
      challengesMode === 'daily'
        ? data.challengesOverTimeDaily
        : data.challengesOverTimeWeekly

    if (!buckets || buckets.length === 0) {
      return
    }

    if (challengesMode === 'daily' && challengesRange === 'last7' && buckets.length > 7) {
      buckets = buckets.slice(-7)
    }

    const instance = echarts.init(challengesRef.current)

    const labels =
      challengesMode === 'daily'
        ? buckets.map(point => formatDateLabel(point.bucketStart))
        : buckets.map(point => formatWeekLabel(point.bucketStart))

    const startedSeries = buckets.map(point => point.startedCount ?? 0)
    const submittedSeries = buckets.map(point => point.submittedCount ?? 0)

    const option: echarts.EChartsOption = {
      tooltip: {
        trigger: 'axis'
      },
      legend: {
        top: 0
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: labels,
        axisLabel: {
          rotate: 45
        }
      },
      yAxis: {
        type: 'value'
      },
      series: [
        {
          name: t('common.started'),
          type: 'bar',
          stack: 'challenges',
          data: startedSeries
        },
        {
          name: t('common.submitted'),
          type: 'bar',
          stack: 'challenges',
          data: submittedSeries
        }
      ]
    }

    instance.setOption(option)

    const handleResize = () => {
      instance.resize()
    }
    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
      instance.dispose()
    }
  }, [data, challengesMode, challengesRange, t])

  // Tasks overview - challenges count (horizontal bar, sorted by count desc)
  useEffect(() => {
    if (!tasksCountRef.current || !data || data.tasksOverview.length === 0) {
      return
    }

    const sortedByCount = [...data.tasksOverview]
      .sort((a, b) => (a.challengesCount ?? 0) - (b.challengesCount ?? 0))
      .slice(tasksRange === 'top5' ? -5 : undefined)

    const instance = echarts.init(tasksCountRef.current)

    const labels = sortedByCount.map(entry => entry.taskName || entry.taskId)
    const challengesCounts = sortedByCount.map(entry => entry.challengesCount ?? 0)

    const option: echarts.EChartsOption = {
      tooltip: {
        trigger: 'axis'
      },
      legend: {
        show: false
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        containLabel: true
      },
      xAxis: {
        type: 'value',
        name: t('admin.statisticsTasksChallengesYAxis')
      },
      yAxis: {
        type: 'category',
        data: labels
      },
      series: [
        {
          name: t('admin.statisticsTasksChallengesSeries'),
          type: 'bar',
          data: challengesCounts
        }
      ]
    }

    instance.setOption(option)

    const handleResize = () => {
      instance.resize()
    }
    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
      instance.dispose()
    }
  }, [data, tasksRange, t])

  // Tasks overview - average grade (horizontal bar, sorted by grade desc)
  useEffect(() => {
    if (!tasksGradeRef.current || !data || data.tasksOverview.length === 0) {
      return
    }

    const sortedByGrade = [...data.tasksOverview]
      .sort((a, b) => (a.averageGrade ?? 0) - (b.averageGrade ?? 0))
      .slice(tasksRange === 'top5' ? -5 : undefined)

    const instance = echarts.init(tasksGradeRef.current)

    const labels = sortedByGrade.map(entry => entry.taskName || entry.taskId)
    const averageGrades = sortedByGrade.map(entry => entry.averageGrade ?? 0)

    const option: echarts.EChartsOption = {
      tooltip: {
        trigger: 'axis'
      },
      legend: {
        show: false
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        containLabel: true
      },
      xAxis: {
        type: 'value',
        name: t('admin.statisticsTasksAverageGradeYAxis'),
        min: 1,
        max: 10
      },
      yAxis: {
        type: 'category',
        data: labels
      },
      series: [
        {
          name: t('admin.statisticsTasksAverageGradeSeries'),
          type: 'bar',
          data: averageGrades
        }
      ]
    }

    instance.setOption(option)

    const handleResize = () => {
      instance.resize()
    }
    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
      instance.dispose()
    }
  }, [data, tasksRange, t])

  // Grade distribution (1-10 histogram) for all tasks or a single selected task
  useEffect(() => {
    if (!gradeDistributionRef.current || !data || data.tasksOverview.length === 0) {
      return
    }

    const selectedTaskId = gradeDistributionTaskIds[0]
    const distribution = new Array<number>(10).fill(0)
    for (const entry of data.tasksOverview) {
      if (selectedTaskId && entry.taskId !== selectedTaskId) continue
      const entryDistribution = entry.gradeDistribution ?? []
      for (let i = 0; i < distribution.length; i++) {
        distribution[i] += entryDistribution[i] ?? 0
      }
    }

    const instance = echarts.init(gradeDistributionRef.current)

    const labels = Array.from({ length: 10 }, (_, i) => String(i + 1))

    const option: echarts.EChartsOption = {
      tooltip: {
        trigger: 'axis'
      },
      legend: {
        show: false
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: labels,
        name: t('admin.statisticsGradeDistributionXAxis')
      },
      yAxis: {
        type: 'value',
        name: t('admin.statisticsGradeDistributionYAxis'),
        minInterval: 1
      },
      series: [
        {
          name: t('admin.statisticsGradeDistributionSeries'),
          type: 'bar',
          data: distribution
        }
      ]
    }

    instance.setOption(option)

    const handleResize = () => {
      instance.resize()
    }
    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
      instance.dispose()
    }
  }, [data, gradeDistributionTaskIds, t])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <StandardLoader size="md" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="space-y-4">
        <h2 className="text-2xl font-bold text-foreground">{t('common.statistics')}</h2>
        <p className="text-sm text-destructive">{t('common.error')}: {error}</p>
      </div>
    )
  }

  if (!data) {
    return (
      <div className="space-y-4">
        <h2 className="text-2xl font-bold text-foreground">{t('common.statistics')}</h2>
        <Card>
          <CardContent className="pt-6">
            <NoDataEmptyState
              title={t('admin.statisticsNoDataTitle')}
              description={t('admin.statisticsNoDataDescription')}
            />
          </CardContent>
        </Card>
      </div>
    )
  }

  const hasAnyData =
    data.userSignupsOverTime.length > 0 ||
    data.challengesOverTimeDaily.length > 0 ||
    data.challengesOverTimeWeekly.length > 0 ||
    data.tasksOverview.length > 0

  if (!hasAnyData) {
    return (
      <div className="space-y-4">
        <h2 className="text-2xl font-bold text-foreground">{t('common.statistics')}</h2>
        <Card>
          <CardContent className="pt-6">
            <NoDataEmptyState
              title={t('admin.statisticsNoDataTitle')}
              description={t('admin.statisticsNoDataDescription')}
            />
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-foreground sm:text-3xl">
          {t('common.statistics')}
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">
          {t('admin.statisticsDescription')}
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card className="h-full">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between mb-4 gap-2">
              <h3 className="text-lg font-semibold text-foreground">
                {t('admin.statisticsUserSignupsOverTime')}
              </h3>
              <div className="inline-flex gap-1">
                <Button
                  type="button"
                  variant={signupsRange === 'last7' ? 'default' : 'outline'}
                  size="sm"
                  className="h-7 px-2 text-xs"
                  onClick={() => setSignupsRange('last7')}
                >
                  {t('admin.statisticsLast7Days')}
                </Button>
                <Button
                  type="button"
                  variant={signupsRange === 'all' ? 'default' : 'outline'}
                  size="sm"
                  className="h-7 px-2 text-xs"
                  onClick={() => setSignupsRange('all')}
                >
                  {t('admin.statisticsAllTime')}
                </Button>
              </div>
            </div>
            <div
              ref={signupsRef}
              className="w-full h-80"
            />
          </CardContent>
        </Card>

        <Card className="h-full">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between mb-4 gap-2">
              <h3 className="text-lg font-semibold text-foreground">
                {t('admin.statisticsChallengesOverTime')}
              </h3>
              <div className="flex flex-col items-end gap-1">
                <div className="inline-flex gap-1">
                  <Button
                    type="button"
                    variant={challengesMode === 'daily' ? 'default' : 'outline'}
                    size="sm"
                    className="h-7 px-2 text-xs"
                    onClick={() => setChallengesMode('daily')}
                  >
                    {t('admin.statisticsDaily')}
                  </Button>
                  <Button
                    type="button"
                    variant={challengesMode === 'weekly' ? 'default' : 'outline'}
                    size="sm"
                    className="h-7 px-2 text-xs"
                    onClick={() => setChallengesMode('weekly')}
                  >
                    {t('admin.statisticsWeekly')}
                  </Button>
                </div>
                {challengesMode === 'daily' && (
                  <div className="inline-flex gap-1">
                    <Button
                      type="button"
                      variant={challengesRange === 'last7' ? 'default' : 'outline'}
                      size="sm"
                      className="h-7 px-2 text-xs"
                      onClick={() => setChallengesRange('last7')}
                    >
                      {t('admin.statisticsLast7Days')}
                    </Button>
                    <Button
                      type="button"
                      variant={challengesRange === 'all' ? 'default' : 'outline'}
                      size="sm"
                      className="h-7 px-2 text-xs"
                      onClick={() => setChallengesRange('all')}
                    >
                      {t('admin.statisticsAllTime')}
                    </Button>
                  </div>
                )}
              </div>
            </div>
            <div
              ref={challengesRef}
              className="w-full h-80"
            />
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-start justify-between mb-4 gap-2">
              <h3 className="text-lg font-semibold text-foreground">
                {t('admin.statisticsTasksOverviewCount')}
              </h3>
              <div className="inline-flex gap-1">
                <Button
                  type="button"
                  variant={tasksRange === 'top5' ? 'default' : 'outline'}
                  size="sm"
                  className="h-7 px-2 text-xs"
                  onClick={() => setTasksRange('top5')}
                >
                  {t('admin.statisticsTop5')}
                </Button>
                <Button
                  type="button"
                  variant={tasksRange === 'all' ? 'default' : 'outline'}
                  size="sm"
                  className="h-7 px-2 text-xs"
                  onClick={() => setTasksRange('all')}
                >
                  {t('admin.statisticsAll')}
                </Button>
              </div>
            </div>
            <div
              ref={tasksCountRef}
              className="w-full h-96"
            />
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-start justify-between mb-4 gap-2">
              <h3 className="text-lg font-semibold text-foreground">
                {t('admin.statisticsTasksOverviewGrade')}
              </h3>
              <div className="inline-flex gap-1">
                <Button
                  type="button"
                  variant={tasksRange === 'top5' ? 'default' : 'outline'}
                  size="sm"
                  className="h-7 px-2 text-xs"
                  onClick={() => setTasksRange('top5')}
                >
                  {t('admin.statisticsTop5')}
                </Button>
                <Button
                  type="button"
                  variant={tasksRange === 'all' ? 'default' : 'outline'}
                  size="sm"
                  className="h-7 px-2 text-xs"
                  onClick={() => setTasksRange('all')}
                >
                  {t('admin.statisticsAll')}
                </Button>
              </div>
            </div>
            <div
              ref={tasksGradeRef}
              className="w-full h-96"
            />
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col items-start justify-between gap-3 mb-4 sm:flex-row sm:items-center">
            <h3 className="text-lg font-semibold text-foreground">
              {t('admin.statisticsGradeDistribution')}
            </h3>
            <TaskDataInput
              singlePick
              selected={gradeDistributionTaskIds}
              onChange={setGradeDistributionTaskIds}
              className="w-full sm:w-72"
              labels={{ placeholder: t('admin.statisticsGradeDistributionAllTasks') }}
            />
          </div>
          <div
            ref={gradeDistributionRef}
            className="w-full h-96"
          />
        </CardContent>
      </Card>
    </div>
  )
}

